/**
 * Servicio de Compresión de Imágenes
 * 
 * Maneja la compresión, redimensionamiento y optimización de imágenes
 * para el sistema de mantenimientos con soporte mobile-first.
 */

import { toast } from 'react-hot-toast';

// Configuraciones de compresión por tipo de dispositivo y conexión
const COMPRESSION_SETTINGS = {
  mobile: {
    '2g': { maxWidth: 800, maxHeight: 800, quality: 0.6 },
    '3g': { maxWidth: 1024, maxHeight: 1024, quality: 0.7 },
    '4g': { maxWidth: 1280, maxHeight: 1280, quality: 0.8 },
    default: { maxWidth: 1280, maxHeight: 1280, quality: 0.8 }
  },
  desktop: {
    default: { maxWidth: 1920, maxHeight: 1920, quality: 0.85 }
  }
};

// Configuración de thumbnails
const THUMBNAIL_SETTINGS = {
  width: 300,
  height: 300,
  quality: 0.7
};

// Tipos MIME permitidos
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/webp'
];

// Tamaño máximo de archivo (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

class ImageCompressionService {
  
  /**
   * Detecta el tipo de dispositivo
   */
  static detectDevice() {
    const isMobile = window.innerWidth < 768 || 'ontouchstart' in window;
    return isMobile ? 'mobile' : 'desktop';
  }

  /**
   * Detecta el tipo de conexión
   */
  static detectConnection() {
    if (!navigator.connection) return 'default';
    
    const connection = navigator.connection;
    return connection.effectiveType || 'default';
  }

  /**
   * Obtiene la configuración de compresión basada en dispositivo y conexión
   */
  static getCompressionSettings() {
    const device = this.detectDevice();
    const connection = this.detectConnection();
    
    if (device === 'mobile') {
      return COMPRESSION_SETTINGS.mobile[connection] || COMPRESSION_SETTINGS.mobile.default;
    }
    
    return COMPRESSION_SETTINGS.desktop.default;
  }

  /**
   * Valida si un archivo es una imagen válida
   */
  static validateImage(file) {
    const errors = [];
    
    // Validar tipo MIME
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      errors.push(`Formato no permitido. Use: ${ALLOWED_MIME_TYPES.join(', ')}`);
    }
    
    // Validar tamaño
    if (file.size > MAX_FILE_SIZE) {
      const maxSizeMB = MAX_FILE_SIZE / (1024 * 1024);
      errors.push(`Archivo muy grande. Máximo: ${maxSizeMB}MB`);
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Crea un canvas a partir de una imagen
   */
  static async createCanvasFromImage(image, width, height) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = width;
    canvas.height = height;
    
    // Configurar calidad de renderizado
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Dibujar imagen redimensionada
    ctx.drawImage(image, 0, 0, width, height);
    
    return canvas;
  }

  /**
   * Calcula las dimensiones finales manteniendo aspect ratio
   */
  static calculateDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
    let { width, height } = { width: originalWidth, height: originalHeight };
    
    // Calcular ratio de redimensionamiento
    const widthRatio = maxWidth / width;
    const heightRatio = maxHeight / height;
    const ratio = Math.min(widthRatio, heightRatio);
    
    // Solo redimensionar si es necesario
    if (ratio < 1) {
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }
    
    return { width, height };
  }

  /**
   * Obtiene orientación de imagen desde datos EXIF
   */
  static getImageOrientation(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const view = new DataView(e.target.result);
        
        if (view.getUint16(0, false) !== 0xFFD8) {
          resolve(1); // No es JPEG, orientación normal
          return;
        }
        
        const length = view.byteLength;
        let offset = 2;
        
        while (offset < length) {
          const marker = view.getUint16(offset, false);
          offset += 2;
          
          if (marker === 0xFFE1) {
            const exifLength = view.getUint16(offset, false);
            offset += 2;
            
            if (view.getUint32(offset, false) === 0x45786966) {
              // Encontrado EXIF
              const little = view.getUint16(offset + 6, false) === 0x4949;
              offset += view.getUint32(offset + 10, little);
              const tags = view.getUint16(offset, little);
              offset += 2;
              
              for (let i = 0; i < tags; i++) {
                if (view.getUint16(offset + (i * 12), little) === 0x0112) {
                  const orientation = view.getUint16(offset + (i * 12) + 8, little);
                  resolve(orientation);
                  return;
                }
              }
            }
            break;
          } else if ((marker & 0xFF00) !== 0xFF00) {
            break;
          } else {
            offset += view.getUint16(offset, false);
          }
        }
        
        resolve(1); // Orientación normal por defecto
      };
      
      reader.readAsArrayBuffer(file.slice(0, 64 * 1024)); // Solo leer primeros 64KB
    });
  }

  /**
   * Aplica corrección de orientación a canvas
   */
  static applyOrientation(canvas, orientation) {
    if (orientation === 1) return canvas; // Sin rotación necesaria
    
    const { width, height } = canvas;
    const newCanvas = document.createElement('canvas');
    const ctx = newCanvas.getContext('2d');
    
    // Configurar dimensiones según orientación
    if (orientation >= 5 && orientation <= 8) {
      newCanvas.width = height;
      newCanvas.height = width;
    } else {
      newCanvas.width = width;
      newCanvas.height = height;
    }
    
    // Aplicar transformaciones según orientación EXIF
    switch (orientation) {
      case 2: // Flip horizontal
        ctx.transform(-1, 0, 0, 1, width, 0);
        break;
      case 3: // Rotar 180°
        ctx.transform(-1, 0, 0, -1, width, height);
        break;
      case 4: // Flip vertical
        ctx.transform(1, 0, 0, -1, 0, height);
        break;
      case 5: // Rotar 90° + flip horizontal
        ctx.transform(0, 1, 1, 0, 0, 0);
        break;
      case 6: // Rotar 90°
        ctx.transform(0, 1, -1, 0, height, 0);
        break;
      case 7: // Rotar 270° + flip horizontal
        ctx.transform(0, -1, -1, 0, height, width);
        break;
      case 8: // Rotar 270°
        ctx.transform(0, -1, 1, 0, 0, width);
        break;
    }
    
    ctx.drawImage(canvas, 0, 0);
    return newCanvas;
  }

  /**
   * Convierte canvas a blob con compresión
   */
  static canvasToBlob(canvas, quality = 0.8, format = 'image/jpeg') {
    return new Promise((resolve) => {
      canvas.toBlob(resolve, format, quality);
    });
  }

  /**
   * Comprime una imagen aplicando configuración automática
   */
  static async compressImage(file, customSettings = null) {
    try {
      // Validar archivo
      const validation = this.validateImage(file);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Obtener configuración
      const settings = customSettings || this.getCompressionSettings();
      
      // Cargar imagen
      const image = await this.loadImage(file);
      
      // Obtener orientación EXIF
      const orientation = await this.getImageOrientation(file);
      
      // Calcular dimensiones finales
      const { width, height } = this.calculateDimensions(
        image.width,
        image.height,
        settings.maxWidth,
        settings.maxHeight
      );
      
      // Crear canvas redimensionado
      let canvas = await this.createCanvasFromImage(image, width, height);
      
      // Aplicar corrección de orientación
      canvas = this.applyOrientation(canvas, orientation);
      
      // Convertir a blob comprimido
      const compressedBlob = await this.canvasToBlob(canvas, settings.quality);
      
      // Crear archivo comprimido
      const compressedFile = new File(
        [compressedBlob],
        file.name,
        { type: compressedBlob.type }
      );

      return {
        originalFile: file,
        compressedFile,
        originalSize: file.size,
        compressedSize: compressedFile.size,
        compressionRatio: ((file.size - compressedFile.size) / file.size * 100).toFixed(1),
        dimensions: { width: canvas.width, height: canvas.height },
        orientation,
        settings
      };

    } catch (error) {
      console.error('Error comprimiendo imagen:', error);
      throw error;
    }
  }

  /**
   * Genera thumbnail de una imagen
   */
  static async generateThumbnail(file, customSettings = null) {
    try {
      const settings = customSettings || THUMBNAIL_SETTINGS;
      
      // Cargar imagen original
      const image = await this.loadImage(file);
      
      // Calcular dimensiones cuadradas (crop center)
      const size = Math.min(image.width, image.height);
      const offsetX = (image.width - size) / 2;
      const offsetY = (image.height - size) / 2;
      
      // Crear canvas para thumbnail
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = settings.width;
      canvas.height = settings.height;
      
      // Configurar calidad
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Dibujar imagen croppeada y redimensionada
      ctx.drawImage(
        image,
        offsetX, offsetY, size, size, // Source (crop square from center)
        0, 0, settings.width, settings.height // Destination (fit to thumbnail size)
      );
      
      // Convertir a blob
      const thumbnailBlob = await this.canvasToBlob(canvas, settings.quality);
      
      // Crear archivo thumbnail
      const thumbnailFile = new File(
        [thumbnailBlob],
        `thumb_${file.name}`,
        { type: thumbnailBlob.type }
      );

      return {
        originalFile: file,
        thumbnailFile,
        thumbnailSize: thumbnailFile.size,
        dimensions: { width: settings.width, height: settings.height }
      };

    } catch (error) {
      console.error('Error generando thumbnail:', error);
      throw error;
    }
  }

  /**
   * Carga una imagen desde archivo
   */
  static loadImage(file) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('No se pudo cargar la imagen'));
      
      image.src = URL.createObjectURL(file);
    });
  }

  /**
   * Procesa múltiples imágenes en lote
   */
  static async processBatch(files, onProgress = null) {
    const results = [];
    const total = files.length;
    
    for (let i = 0; i < files.length; i++) {
      try {
        const file = files[i];
        
        // Comprimir imagen principal
        const compressed = await this.compressImage(file);
        
        // Generar thumbnail
        const thumbnail = await this.generateThumbnail(file);
        
        results.push({
          success: true,
          originalFile: file,
          compressed: compressed.compressedFile,
          thumbnail: thumbnail.thumbnailFile,
          metadata: {
            originalSize: compressed.originalSize,
            compressedSize: compressed.compressedSize,
            thumbnailSize: thumbnail.thumbnailSize,
            compressionRatio: compressed.compressionRatio,
            dimensions: compressed.dimensions,
            thumbnailDimensions: thumbnail.dimensions
          }
        });
        
        // Callback de progreso
        if (onProgress) {
          onProgress({
            current: i + 1,
            total,
            percentage: Math.round(((i + 1) / total) * 100),
            currentFile: file.name
          });
        }
        
      } catch (error) {
        console.error(`Error procesando ${files[i].name}:`, error);
        results.push({
          success: false,
          originalFile: files[i],
          error: error.message
        });
      }
    }
    
    return results;
  }

  /**
   * Obtiene información básica de un archivo de imagen
   */
  static async getImageInfo(file) {
    try {
      const image = await this.loadImage(file);
      const orientation = await this.getImageOrientation(file);
      
      return {
        name: file.name,
        size: file.size,
        type: file.type,
        dimensions: {
          width: image.width,
          height: image.height
        },
        orientation,
        aspectRatio: (image.width / image.height).toFixed(2)
      };
    } catch (error) {
      throw new Error(`Error obteniendo información de imagen: ${error.message}`);
    }
  }
}

export default ImageCompressionService;